import { useDynamicContext, useUserWallets } from '@dynamic-labs/sdk-react-core';
import { useMutation } from '@tanstack/react-query';
import { CheckCircle, Rocket } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useProjectsContext } from 'src/modules/projects/context';
import { useDeployFuulContract } from 'src/modules/projects/hooks/useDeployFuulContract';
import { SolanaDeployStatus, useSolanaDeployProject } from 'src/modules/projects/hooks/useSolanaDeployProject';
import { projectService } from 'src/modules/projects/infra/projectService';
import { useWaitForTransactionReceipt } from 'wagmi';

import { getSolanaExplorerTxUrl, getSolanaNetworkConfig, isSolanaChainId, SolanaChainId } from '@/config/solana';
import { isStellarChainId, StellarChainId } from '@/config/stellar';
import { Avatar, AvatarFallback, Button, Link as DSLink, Spinner, Text } from '@/design-system/components/ui';
import { handleContractErrorToasts } from '@/utils/contract-errors';

import SwitchNetworkBeforeProceedingButton from '../../modules/conversions/components/SwitchNetworkBeforeProceedingButton';
import { Callout } from './callouts/Callout';
import { SimpleModal } from './modal';
import { StellarDeploySection } from './StellarDeploySection';

interface Props {
  open: boolean;
  handleClose: () => void;
  closeOnClick?: boolean;
}

// Status messages for Solana deployment
const SOLANA_STATUS_MESSAGES: Record<SolanaDeployStatus, string> = {
  idle: 'Ready to initialize',
  loading: 'Preparing transaction...',
  signing: 'Please confirm in your wallet...',
  confirming: 'Confirming transaction...',
  success: 'Project initialized!',
  error: 'Deployment failed',
};

// Solana deployment section component
interface SolanaDeploySectionProps {
  chainId: SolanaChainId;
  projectId: string;
  onSuccess: () => void;
}

const SolanaDeploySection = ({ chainId, projectId, onSuccess }: SolanaDeploySectionProps) => {
  const networkConfig = getSolanaNetworkConfig(chainId);
  const userWallets = useUserWallets();
  const { deploy, status, signature, projectPda, error, reset, isLoading } = useSolanaDeployProject();
  const [isPolling, setIsPolling] = useState(false);

  // Find Solana wallet from all connected wallets (not just primary)
  const solanaWallet = useMemo(() => {
    return userWallets?.find((w) => w.chain === 'SOL') ?? null;
  }, [userWallets]);

  const uploadMetadataMutation = useMutation({
    mutationFn: () => projectService.uploadMetadata(projectId),
  });

  // Poll backend for sync - WITH CLEANUP
  useEffect(() => {
    if (status !== 'success' || !signature) return;

    let cancelled = false;
    let timeoutId: NodeJS.Timeout | null = null;
    let retries = 0;
    const maxRetries = 15;

    setIsPolling(true);

    const pollProject = async () => {
      if (cancelled) return;

      try {
        const project = await projectService.getById(projectId);
        if (cancelled) return;

        if (project.contract_address) {
          setIsPolling(false);
          onSuccess();
        } else if (retries < maxRetries) {
          retries++;
          timeoutId = setTimeout(pollProject, 2000);
        } else {
          // Timeout - on-chain succeeded, backend will sync
          setIsPolling(false);
          onSuccess();
        }
      } catch {
        if (!cancelled && retries < maxRetries) {
          retries++;
          timeoutId = setTimeout(pollProject, 2000);
        }
      }
    };

    pollProject();

    // CLEANUP: Prevent memory leaks
    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [status, signature, projectId, onSuccess]);

  const handleDeploy = async () => {
    const { metadata_uri } = await uploadMetadataMutation.mutateAsync();
    deploy({ chainId, metadataUri: metadata_uri, projectId });
  };

  const hasSolanaWallet = !!solanaWallet;

  if (!hasSolanaWallet) {
    return (
      <Callout
        type="warning"
        title="SVM Wallet Required"
        body="Connected user is not SVM. Invite an SVM address to the team and login with that address to initialize the project."
      />
    );
  }

  return (
    <div className="tw:flex tw:flex-col tw:gap-4">
      <Text variant="body-md" color="muted">
        Initialize your project on {networkConfig.name} (~0.01 {networkConfig.nativeCurrencySymbol} transaction fee).
      </Text>

      {/* Status */}
      <div className="tw:flex tw:items-center tw:gap-2">
        {isLoading && <Spinner />}
        {status === 'success' && <CheckCircle size={20} color="green" />}
        <Text variant="body-md">{SOLANA_STATUS_MESSAGES[status]}</Text>
      </div>

      {/* Explorer Link */}
      {signature && (
        <DSLink href={getSolanaExplorerTxUrl(signature, chainId)} external>
          View on {networkConfig.explorerName}
        </DSLink>
      )}

      {/* In-progress message */}
      {(status === 'confirming' || isPolling) && (
        <Callout
          type="info"
          title="Transaction in progress"
          body="This may take a few minutes. Please don't close this window or try to deploy again."
        />
      )}

      {/* Success */}
      {projectPda && status === 'success' && !isPolling && (
        <Callout type="success" title="Project Address" body={`${projectPda.slice(0, 8)}...${projectPda.slice(-8)}`} />
      )}

      {/* Error */}
      {error && <Callout type="error" title="Failed" body={error.message} />}

      {/* Actions */}
      <div className="tw:flex tw:flex-row tw:gap-2">
        <Button
          variant="primary"
          onClick={handleDeploy}
          disabled={isLoading || status === 'success' || isPolling || uploadMetadataMutation.isPending}
        >
          {status === 'success' ? 'Done' : isLoading ? 'Processing...' : `Initialize on ${networkConfig.name}`}
        </Button>
        {error && (
          <Button variant="outline" onClick={reset}>
            Retry
          </Button>
        )}
      </div>
    </div>
  );
};

const DeployContractModal = ({ open, handleClose, closeOnClick }: Props) => {
  const { project, refetchProject } = useProjectsContext();
  const { network: connectedNetwork } = useDynamicContext();
  const [isPollingProject, setIsPollingProject] = useState<boolean>(false);

  // Detect if this is a Solana network using numeric chainId
  const isSolana = useMemo(() => {
    return project?.contractChainId != null && isSolanaChainId(project.contractChainId);
  }, [project?.contractChainId]);

  const isStellar = useMemo(() => {
    return project?.contractChainId != null && isStellarChainId(project.contractChainId);
  }, [project?.contractChainId]);

  // Writes through the Dynamic wallet directly (not the wagmi connector) to avoid
  // ConnectorNotConnectedError when the Dynamic <-> wagmi bridge is out of sync.
  const {
    deployContract,
    txHash: deployedContractData,
    writeError: deployContractError,
    isSigning: isLoadingDeployContractWrite,
  } = useDeployFuulContract();

  const [isMutationLoading, setIsMutationLoading] = useState(false);
  const uploadProjectMetadataMutation = useMutation({
    mutationFn: async () => await projectService.uploadMetadata(project?.id as string),
    onMutate: () => {
      setIsMutationLoading(true);
    },
    onSuccess: async ({ metadata_uri }) => {
      setIsMutationLoading(false);
      // V2 contracts: createFuulProject(projectAdmin, projectInfoURI, kycRequired)
      try {
        await deployContract({ chainId: project?.contractChainId, metadataUri: metadata_uri });
      } catch {
        // Error surfaced via deployContractError + handleContractErrorToasts effect.
      }
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const {
    isLoading: isLoadingTransaction,
    error: transactionError,
    isSuccess: isSuccessTransaction,
  } = useWaitForTransactionReceipt({
    hash: deployedContractData,
    confirmations: 3,
    chainId: project?.contractChainId ? Number(project.contractChainId) : undefined,
  });

  useEffect(() => {
    if (isSuccessTransaction) {
      setIsPollingProject(true);

      let retries = 0;
      const maxRetries = 10;

      const pollProject = async () => {
        const { contract_address } = await projectService.getById(project?.id as string);

        if (contract_address) {
          setIsPollingProject(false);
          await refetchProject();

          toast.success('Successfully initialized project!', {
            position: 'bottom-right',
          });

          handleClose();
        } else if (retries < maxRetries) {
          retries++;
          setTimeout(pollProject, 1500);
        } else {
          toast.success('Successfully initialized project!', {
            position: 'bottom-right',
          });
          setIsPollingProject(false);
        }
      };

      pollProject();
    }
  }, [isSuccessTransaction]);

  const deployContractButtonText = useMemo(() => {
    if (isLoadingDeployContractWrite) return 'Confirm in wallet...';
    if (isLoadingTransaction) return 'Waiting for confirmation...';
    if (isPollingProject) return 'Waiting for project to be initialized...';
    if (isSuccessTransaction) return 'Close';

    return 'Initialize';
  }, [isLoadingTransaction, isSuccessTransaction, isPollingProject, isLoadingDeployContractWrite]);

  const isButtonDisabled =
    isLoadingTransaction || isPollingProject || isMutationLoading || isLoadingDeployContractWrite;

  useEffect(() => {
    handleContractErrorToasts(transactionError);
    handleContractErrorToasts(deployContractError);
  }, [transactionError, deployContractError]);

  const handleInitializeProject = () => uploadProjectMetadataMutation.mutate();

  const shouldChangeNetworkBeforeProceeding = String(connectedNetwork) !== String(project?.contractChainId);

  return (
    <>
      <SimpleModal
        open={open}
        title={
          <div className="tw:flex tw:flex-row tw:gap-4 tw:items-center">
            <Avatar size="lg">
              <AvatarFallback>
                <Rocket />
              </AvatarFallback>
            </Avatar>
            <div className="tw:flex tw:flex-col tw:gap-1">
              <Text variant="title-xs" as="h2">
                Initialize your incentives program
              </Text>
              <Text variant="body-md" color="muted">
                Deploy your program onchain
              </Text>
            </div>
          </div>
        }
        onClose={() => closeOnClick && handleClose()}
        disableAutoFocus={true}
        disableRestoreFocus={true}
        actions={
          // Solana/Stellar: actions are handled by their deploy section components
          isSolana || isStellar ? undefined : project?.contractChainId && shouldChangeNetworkBeforeProceeding ? (
            <SwitchNetworkBeforeProceedingButton
              shouldSwitchTo={project?.contractChainId}
              styles={{ marginLeft: 'auto' }}
            />
          ) : (
            <Button
              variant="primary"
              disabled={isButtonDisabled}
              onClick={() => {
                if (isSuccessTransaction) {
                  handleClose();
                } else {
                  handleInitializeProject();
                }
              }}
              className="tw:ml-auto"
            >
              {deployContractButtonText}
            </Button>
          )
        }
      >
        {/* CONDITIONAL: Solana vs Stellar vs EVM deployment */}
        {isSolana ? (
          // SOLANA DEPLOYMENT
          <SolanaDeploySection
            chainId={project?.contractChainId as unknown as SolanaChainId}
            projectId={project?.id as string}
            onSuccess={() => {
              refetchProject();
              toast.success('Successfully initialized project!');
              handleClose();
            }}
          />
        ) : isStellar ? (
          <StellarDeploySection
            chainId={Number(project?.contractChainId) as StellarChainId}
            projectId={project?.id as string}
            onSuccess={() => {
              refetchProject();
              toast.success('Successfully initialized project!');
              handleClose();
            }}
            onClose={handleClose}
          />
        ) : (
          // EVM DEPLOYMENT
          <div className="tw:flex tw:flex-col tw:gap-2">
            <Text variant="body-md" color="muted">
              Initializing your incentives program involves a one-time gas fee to deploy and activate it on the
              blockchain.{' '}
              <DSLink
                href="https://fuul-app.notion.site/Fuul-Contracts-Migration-Guide-2cda045a4b2880de9053cbcf0611c6e9"
                external
              >
                Learn more about V2 contracts
              </DSLink>
            </Text>
            {(isLoadingTransaction || isPollingProject) && (
              <Callout
                type="info"
                title="Transaction in progress"
                body="This may take a few minutes. Please don't close this window or try to deploy again."
              />
            )}
            {isSuccessTransaction && !isPollingProject && (
              <Callout
                type="info"
                title={'Your contract was successfully deployed and is now being processed.'}
                body={'Please contact us for support on linking the contract to your project.'}
              />
            )}
          </div>
        )}
      </SimpleModal>
    </>
  );
};

export default DeployContractModal;
